import React, { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from './components/PageHeader';
import { JsonTree } from './components/JsonTree';
import { searchFacilitiesByLicense } from './hooks/useFacilities';

// ── Types ──────────────────────────────────────────────────────────────────────

interface FacilityDocument {
  id: number;
  organizationId: number;
  organization?: OrganizationSummary;
  marketId: number;
  name: string;
  license: string;
  metrcFacilityId: number;
  active: boolean;
  createdAt?: string;
  lastModified?: string;
}

interface OrganizationSummary {
  id: number;
  name?: string;
}

interface PagedResult<T> {
  page: number;
  pageSize: number;
  total: number;
  data: T[];
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmtDate(val: string | null | undefined): string {
  if (!val) return '';
  try {
    return new Date(val).toLocaleString();
  } catch {
    return val;
  }
}

function fmtVal(val: unknown): string | null {
  if (val === null || val === undefined || val === '') return null;
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  return String(val);
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function KvTable({ rows }: { rows: Array<[string, unknown]> }) {
  const visible = rows.filter(([, v]) => fmtVal(v) !== null);
  if (visible.length === 0) return null;
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
      <tbody>
        {visible.map(([label, value], i) => (
          <tr key={label} style={{ borderBottom: '1px solid #f0f0f0', background: i % 2 === 0 ? 'white' : '#f8f9fa' }}>
            <td
              style={{
                padding: '6px 10px',
                color: '#888',
                fontWeight: 500,
                width: '38%',
                verticalAlign: 'top',
                whiteSpace: 'nowrap',
              }}
            >
              {label}
            </td>
            <td style={{ padding: '6px 10px', verticalAlign: 'top' }}>{fmtVal(value)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Section({
  title,
  defaultOpen = true,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginTop: '1rem' }}>
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        aria-expanded={open}
        aria-label={`${open ? 'Collapse' : 'Expand'} node ${title}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          width: '100%',
          background: '#c3c4c5',
          border: '1px solid #e8e8e8',
          borderRadius: '4px',
          cursor: 'pointer',
          padding: '0.5rem 0.75rem',
          fontWeight: 600,
          fontSize: '0.84rem',
          color: 'var(--brand)',
          textAlign: 'left',
        }}
      >
        <span style={{ fontSize: '0.6rem' }}>{open ? '▼' : '▶'}</span>
        <span style={{ flex: 1 }}>{title}</span>
      </button>
      {open && <div style={{ paddingTop: '0.25rem' }}>{children}</div>}
    </div>
  );
}

function FacilityCard({ facility, index }: { facility: FacilityDocument; index: number }) {
  const [open, setOpen] = useState(true);
  const organizationLabel = facility.organization
    ? `Org: ${facility.organization.name || 'Unknown'} (ID ${facility.organization.id})`
    : `Org ID ${facility.organizationId}`;

  return (
    <div className="card" style={{ padding: '1.5rem' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '0.75rem',
          marginBottom: '0.75rem',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: '1rem' }}>{facility.name}</div>
          <div style={{ fontSize: '0.85rem', color: '#555', marginTop: '0.15rem', fontFamily: 'monospace' }}>
            {facility.license}
          </div>
        </div>
        <span className="badge" style={{ background: '#eef6ff', color: '#075985', fontWeight: 700 }}>
          {organizationLabel}
        </span>
        <div style={{ fontSize: '0.8rem', color: '#999', whiteSpace: 'nowrap' }}>
          #{index + 1} · {facility.active ? 'Active' : 'Inactive'}
        </div>
        <button
          type="button"
          onClick={() => setOpen((p) => !p)}
          style={{
            background: 'none',
            border: '1px solid #e8e8e8',
            borderRadius: '4px',
            cursor: 'pointer',
            padding: '0.25rem 0.6rem',
            fontSize: '0.75rem',
            color: 'var(--brand)',
          }}
        >
          {open ? 'Collapse' : 'Expand'}
        </button>
      </div>

      {open && (
        <Section title="Details" defaultOpen={true}>
          <KvTable
            rows={[
              ['ID', facility.id],
              ['Organization ID', facility.organizationId],
              ['Organization Name', facility.organization?.name],
              ['Market ID', facility.marketId],
              ['License', facility.license],
              ['Metrc Facility ID', facility.metrcFacilityId],
              ['Active', facility.active],
              ['Created At', fmtDate(facility.createdAt)],
              ['Last Modified', fmtDate(facility.lastModified)],
            ]}
          />
        </Section>
      )}
    </div>
  );
}

function ViewToggle({
  mode,
  onChange,
}: {
  mode: 'structured' | 'raw';
  onChange: (m: 'structured' | 'raw') => void;
}) {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
      <button
        type="button"
        className={`button${mode === 'structured' ? ' cta' : ''}`}
        onClick={() => onChange('structured')}
      >
        Structured
      </button>
      <button
        type="button"
        className={`button${mode === 'raw' ? ' cta' : ''}`}
        onClick={() => onChange('raw')}
      >
        Raw JSON
      </button>
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

export default function FacilitySearchPage() {
  const navigate = useNavigate();
  const [license, setLicense] = useState('');
  const [submittedLicense, setSubmittedLicense] = useState('');
  const [result, setResult] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [viewMode, setViewMode] = useState<'structured' | 'raw'>('structured');

  const fetchPage = async (searchLicense: string, pageNum: number) => {
    if (!searchLicense) return;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const data = await searchFacilitiesByLicense(searchLicense, pageNum, 50);
      setResult(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = license.trim();
    if (!trimmed) return;

    setSubmittedLicense(trimmed);
    setPage(0);
    await fetchPage(trimmed, 0);
  };

  const paged = result as PagedResult<FacilityDocument> | null;
  const facilities = paged?.data ?? null;
  const totalPages = paged && paged.pageSize > 0 ? Math.ceil(paged.total / paged.pageSize) : 0;

  return (
    <div className="stack" data-testid="page-facility-search">
      <PageHeader
        title="Facility Search"
        subtitle="Search for facilities by license number"
        actions={
          <button type="button" className="button" onClick={() => navigate('/internal')}>
            Back
          </button>
        }
      />

      <div className="card" style={{ padding: '2rem' }}>
        <form className="stack" onSubmit={handleSearch}>
          <label htmlFor="facility-name-input" style={{ fontWeight: 600 }}>
            License Number
          </label>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <input
              id="facility-name-input"
              type="text"
              className="input"
              placeholder="Enter license number…"
              value={license}
              onChange={(e) => setLicense(e.target.value)}
              disabled={loading}
              style={{ flex: 1 }}
              data-testid="facility-name-input"
            />
            <button
              type="submit"
              className="button cta"
              disabled={loading || !license.trim()}
              data-testid="facility-search-btn"
            >
              {loading ? 'Searching…' : 'Search'}
            </button>
          </div>
        </form>
      </div>

      {error && (
        <div className="card" style={{ padding: '2rem' }}>
          <p style={{ color: 'var(--error, red)' }} data-testid="facility-search-error">
            {error}
          </p>
        </div>
      )}

      {result !== null && (
        <div className="stack">
          <ViewToggle mode={viewMode} onChange={setViewMode} />

          {viewMode === 'structured' ? (
            facilities ? (
              <>
                <div
                  className="card"
                  style={{ padding: '0.75rem 1.25rem', fontSize: '0.85rem', color: '#666' }}
                >
                  {paged!.total === 0
                    ? 'No facilities found.'
                    : `${paged!.total} facilit${paged!.total === 1 ? 'y' : 'ies'} found for license “${submittedLicense}”`}
                </div>
                {facilities.map((f, i) => (
                  <FacilityCard key={f.id} facility={f} index={i} />
                ))}
                {totalPages > 1 && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.75rem',
                      padding: '0.25rem 0',
                    }}
                  >
                    <button
                      type="button"
                      className="button"
                      disabled={page === 0 || loading}
                      onClick={() => { const prev = page - 1; setPage(prev); fetchPage(submittedLicense, prev); }}
                    >
                      ← Previous
                    </button>
                    <span style={{ fontSize: '0.85rem', color: '#666' }}>
                      Page {page + 1} of {totalPages}
                    </span>
                    <button
                      type="button"
                      className="button"
                      disabled={page >= totalPages - 1 || loading}
                      onClick={() => { const next = page + 1; setPage(next); fetchPage(submittedLicense, next); }}
                    >
                      Next →
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="card" style={{ padding: '1.5rem' }}>
                <JsonTree value={result} />
              </div>
            )
          ) : (
            <div className="card" style={{ padding: '2rem' }}>
              <textarea
                id="facility-search-output"
                readOnly
                value={JSON.stringify(result, null, 2)}
                rows={24}
                style={{
                  width: '100%',
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                  resize: 'vertical',
                }}
                data-testid="facility-search-output"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
